import React, { useCallback, useMemo } from 'react';
import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, useWindowDimensions, Platform, type TextStyle } from 'react-native';

import Svg, { Circle, Defs, G, LinearGradient, Stop } from 'react-native-svg';
import moment from 'moment';
import { useTranslation, formatNumber, getLocale } from '../../../../shared/i18n';
import { useRtl } from '../../../../shared/i18n/useRtl';
import type { ThemeColors } from '../../../../shared/theme';
import { ContentCard } from '../../../../components/common/ContentCard';
import { Clickable } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { appTheme } from '../../../../shared/theme';
import { hexToRgba } from '../../../../shared/theme/colorUtils';
import { useHealthInformationCms } from '../../../../healthInformation/components/HealthInformationCmsContext';
import { useCaloriesLimitVisible } from '../../../../data/domains/config';
import { resolveDashboardCalorieGoal, useSubscriptionContext } from '../../../../data/domains/subscription';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../../../shared/theme/studio/dashboardConstants';

const REF_WIDTH = 390;

type NutritionCardMetrics = ReturnType<typeof buildNutritionCardMetrics>;

function buildNutritionCardMetrics(window_width: number, font_scale: number) {
  const ratio = window_width / REF_WIDTH;
  const scale = Math.min(1.06, Math.max(0.86, ratio)) / Math.max(font_scale, 1);
  const ss = (size: number) => Math.round(size * scale);
  const sf = (size: number) => Math.round(size * scale * 10) / 10;

  const ring_size = ss(120);
  const ring_stroke = ss(9);
  const health_info_btn_size = ss(22);

  return {
    ring_size,
    ring_stroke,
    health_info_btn_size,
    health_info_mark_font: sf(13),
    health_info_mark_line: sf(15),
    ring_kcal_font: sf(22),
    ring_kcal_line: sf(26),
    ring_kcal_label_font: sf(10),
    ring_kcal_label_line: sf(12),
    ring_goal_font: sf(10),
    ring_goal_line: sf(12),
    macro_line_gap: ss(6),
    macro_line_inner_gap: ss(3),
    macro_short_font: sf(12),
    macro_short_line: sf(14),
    macro_short_width: ss(18),
    macro_grams_font: sf(14),
    macro_grams_line: sf(17),
    macro_label_font: sf(10),
    macro_label_line: sf(12),
    macro_track_min_height: ss(4),
    macro_block_height: ring_size,
    brand_icon_size: ss(40),
    brand_icon_radius: ss(14),
    card_overline_font: sf(11),
    date_sub_font: sf(12),
    empty_text_font: sf(13),
    empty_text_line: sf(20),
  };
}

interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

interface DayConfigStub {
  min_calories?: number;
  max_calories?: number;
}

interface DashboardNutritionCardProps {
  macro_totals: MacroTotals;
  day_config: DayConfigStub | null;
  selected_date: string;
  colors: ThemeColors;
  is_empty: boolean;
}

function macroCaloriesFromG(p: number, c: number, f: number) {
  return { p: p * 4, c: c * 4, f: f * 9 };
}

function MacroLine({
  label,
  short,
  grams,
  frac,
  color,
  track,
  colors,
  styles: s,
}: {
  label: string;
  short: string;
  grams: number;
  frac: number;
  color: string;
  track: string;
  colors: ThemeColors;
  styles: ReturnType<typeof createNutritionCardStyles>;
}) {
  const w = `${Math.round(Math.max(0, Math.min(1, frac)) * 100)}%` as const;
  return (
    <View style={s.macroLine}>
      <View style={s.macroLineHead}>
        <Text style={[s.macroLineShort, { color }]}>{short}</Text>
        <Text style={[s.macroLineGrams, { color: colors.text }]} numberOfLines={1}>
          {formatNumber(grams)}g
        </Text>
      </View>
      <View style={[s.macroLineTrack, { backgroundColor: track }]}>
        <View style={[{ width: w, backgroundColor: color, height: '100%' }, s.macroLineFill]} />
      </View>
      <Text style={[s.macroLineLabel, { color: colors.textSecondary }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

const ANDROID_HEALTH_MARK_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export function DashboardNutritionCardSet3({ macro_totals, day_config, selected_date, colors, is_empty }: DashboardNutritionCardProps) {
  const { t } = useTranslation();
  const navigation = useAuthNavigation();
  const rtl = useRtl();
  const { isAvailable: health_info_available } = useHealthInformationCms();
  const calories_limit_enabled = useCaloriesLimitVisible();
  const { activeSubscriptions } = useSubscriptionContext();
  const { width: window_width, fontScale: font_scale } = useWindowDimensions();
  const metrics = useMemo(() => buildNutritionCardMetrics(window_width, font_scale), [window_width, font_scale]);
  const styles = useMemo(() => createNutritionCardStyles(metrics), [metrics]);
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const track = hexToRgba(colors.text, 0.08) as string;
  const { calories, protein, carbs, fats } = macro_totals;
  const cal_goal = resolveDashboardCalorieGoal({
    calories_limit_enabled,
    day_config,
    subscription: activeSubscriptions[0] ?? null,
  });
  const locale = getLocale();
  const date_label = selected_date
    ? (() => {
        try {
          moment.locale(locale === 'ar' ? 'ar' : 'en');
          return moment(selected_date, 'YYYY-MM-DD').format('MMM D');
        } catch {
          moment.locale('en');
          return moment(selected_date, 'YYYY-MM-DD').format('MMM D');
        }
      })()
    : t('dashboard.today');

  const open_health_sources = useCallback(() => {
    (navigation as { navigate: (name: 'HealthInformationSources') => void }).navigate('HealthInformationSources');
  }, [navigation]);

  const { p_r, c_r, f_r } = useMemo(() => {
    const m = macroCaloriesFromG(protein, carbs, fats);
    const s = m.p + m.c + m.f;
    if (s <= 0) {
      return { p_r: 0, c_r: 0, f_r: 0 };
    }
    return { p_r: m.p / s, c_r: m.c / s, f_r: m.f / s };
  }, [protein, carbs, fats]);

  const { ring_size, ring_stroke } = metrics;
  const r_mid = (ring_size - ring_stroke) / 2;
  const c_len = 2 * Math.PI * r_mid;
  const use_goal_ring = cal_goal > 0;
  const cal_ratio = use_goal_ring ? Math.min(1, calories / cal_goal) : 0;
  const composition_segments = useMemo(() => {
    if (use_goal_ring) {
      return [] as Array<{ color: string; frac: number }>;
    }
    const parts = [
      { color: colors.protein, frac: p_r },
      { color: colors.carbs, frac: c_r },
      { color: colors.fats, frac: f_r },
    ].filter(part => part.frac > 0.001);
    return parts;
  }, [use_goal_ring, colors.protein, colors.carbs, colors.fats, p_r, c_r, f_r]);
  const composition_arcs = useMemo(() => {
    if (composition_segments.length === 0) {
      return [] as Array<{ color: string; dash: string; offset: number }>;
    }
    const gap_len =
      composition_segments.length > 1 ? Math.min(c_len * 0.025, ring_stroke * 0.55) : 0;
    const usable = Math.max(0, c_len - gap_len * composition_segments.length);
    let cursor = 0;
    return composition_segments.map(seg => {
      const len = Math.max(0, seg.frac * usable);
      const arc = {
        color: seg.color,
        dash: `${len} ${Math.max(0, c_len - len)}`,
        offset: -cursor,
      };
      cursor += len + gap_len;
      return arc;
    });
  }, [composition_segments, c_len, ring_stroke]);
  const health_btn_bg = colors.surfaceElevated;

  const health_info_button = health_info_available ? (
    <Clickable
      onPress={open_health_sources}
      style={[
        styles.healthInfoBtn,
        {
          width: metrics.health_info_btn_size,
          height: metrics.health_info_btn_size,
          borderRadius: metrics.health_info_btn_size / 2,
          backgroundColor: health_btn_bg,
        },
      ]}
      accessibilityLabel={t('dashboard.macrosHealthSourcesAccessibility')}
      accessibilityRole="button"
      haptics>
      <Text
        style={[
          styles.healthInfoMark,
          { color: colors.primary },
          ANDROID_HEALTH_MARK_TEXT,
        ]}>
        !
      </Text>
    </Clickable>
  ) : null;

  const card_title_row = (
    <View style={styles.cardHeaderRow}>
      <Text
        style={[
          styles.cardOverline,
          styles.cardOverlineInRow,
          { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
        ]}
        numberOfLines={1}
        accessibilityRole="header">
        {t('dashboard.todaysMacros')}
      </Text>
      {health_info_button}
    </View>
  );

  if (is_empty) {
    return (
      <ContentCard
        variant="outlined"
        borderless
        padding="lg"
        style={[studioCardShell(colors), { borderRadius: DASH_STUDIO_RADIUS, backgroundColor: colors.surface }]}>
        {card_title_row}
        <View style={[styles.hRow, { marginTop: appTheme.spacing.sm }]}>
          <View style={[styles.brandIcon, { backgroundColor: colors.surfaceLight }]}>
            <SvgIcon name="flame" size={20} color={colors.calorie || colors.primary} />
          </View>
          <View style={styles.hBody}>
            <Text
              style={[
                styles.emptyText,
                { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
              ]}>
              {t('dashboard.selectMealsHint').replace('{{date}}', date_label)}
            </Text>
          </View>
        </View>
      </ContentCard>
    );
  }

  const grad_id = 'nutriRingGrad';

  return (
    <ContentCard
      variant="outlined"
      borderless
      padding="lg"
      style={[studioCardShell(colors), { borderRadius: DASH_STUDIO_RADIUS, backgroundColor: colors.surface }]}>
      {card_title_row}
      <Text
        style={[
          styles.dateSub,
          { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
        ]}>
        {date_label}
      </Text>

      <View style={styles.hero}>
        <View style={styles.ringBox}>
          <Svg width={ring_size} height={ring_size} style={styles.ringSvg}>
            {use_goal_ring ? (
              <Defs>
                <LinearGradient id={grad_id} x1="0" y1="0" x2="1" y2="1">
                  <Stop offset="0" stopColor={colors.calorie || colors.primary} stopOpacity="1" />
                  <Stop offset="1" stopColor={colors.primary} stopOpacity="0.85" />
                </LinearGradient>
              </Defs>
            ) : null}
            <Circle
              cx={ring_size / 2}
              cy={ring_size / 2}
              r={r_mid}
              stroke={track}
              strokeWidth={ring_stroke}
              fill="none"
            />
            <G transform={`rotate(-90 ${ring_size / 2} ${ring_size / 2})`}>
              {use_goal_ring ? (
                <Circle
                  cx={ring_size / 2}
                  cy={ring_size / 2}
                  r={r_mid}
                  stroke={`url(#${grad_id})`}
                  strokeWidth={ring_stroke}
                  fill="none"
                  strokeDasharray={`${c_len * cal_ratio} ${c_len}`}
                  strokeLinecap="round"
                />
              ) : (
                composition_arcs.map((arc, index) => (
                  <Circle
                    key={`comp-${index}`}
                    cx={ring_size / 2}
                    cy={ring_size / 2}
                    r={r_mid}
                    stroke={arc.color}
                    strokeWidth={ring_stroke}
                    fill="none"
                    strokeDasharray={arc.dash}
                    strokeDashoffset={arc.offset}
                    strokeLinecap="butt"
                  />
                ))
              )}
            </G>
          </Svg>
          <View style={styles.ringCenter} pointerEvents="none">
            <Text style={[styles.ringKcal, { color: colors.text }]} numberOfLines={1}>
              {formatNumber(calories)}
            </Text>
            <Text style={[styles.ringKcalLabel, { color: colors.textSecondary }]} numberOfLines={1}>
              {t('dashboard.kcal')}
            </Text>
            {use_goal_ring ? (
              <Text style={[styles.ringGoal, { color: colors.textLight }]} numberOfLines={1}>
                {t('dashboard.goal')} {formatNumber(cal_goal)}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={styles.macroBlockWrap} pointerEvents="box-none">
          <View style={styles.macroBlock} pointerEvents="none">
            <MacroLine
              label={t('dashboard.protein')}
              short="P"
              grams={protein}
              frac={p_r}
              color={colors.protein}
              track={track}
              colors={colors}
              styles={styles}
            />
            <MacroLine
              label={t('dashboard.carbs')}
              short="C"
              grams={carbs}
              frac={c_r}
              color={colors.carbs}
              track={track}
              colors={colors}
              styles={styles}
            />
            <MacroLine
              label={t('dashboard.fat')}
              short="F"
              grams={fats}
              frac={f_r}
              color={colors.fats}
              track={track}
              colors={colors}
              styles={styles}
            />
          </View>
        </View>
      </View>
    </ContentCard>
  );
}

function createNutritionCardStyles(m: NutritionCardMetrics) {
  return StyleSheet.create({
    cardOverline: {
      fontSize: m.card_overline_font,
      fontWeight: '800',
      letterSpacing: 0.7,
      textTransform: 'uppercase' as const,
    },
    cardHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: appTheme.spacing.sm,
    },
    cardOverlineInRow: {
      flex: 1,
      minWidth: 0,
    },
    dateSub: { fontSize: m.date_sub_font, fontWeight: '600', marginTop: 6, marginBottom: appTheme.spacing.md },
    hRow: { flexDirection: 'row', alignItems: 'flex-start', gap: appTheme.spacing.md },
    hBody: { flex: 1, minWidth: 0 },
    brandIcon: {
      width: m.brand_icon_size,
      height: m.brand_icon_size,
      borderRadius: m.brand_icon_radius,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyText: { ...appTheme.typography.bodySmall, fontSize: m.empty_text_font, lineHeight: m.empty_text_line },
    hero: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.lg,
      minHeight: m.ring_size,
    },
    ringBox: { width: m.ring_size, height: m.ring_size, position: 'relative', flexShrink: 0 },
    ringSvg: { position: 'absolute', left: 0, top: 0 },
    ringCenter: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: m.macro_line_inner_gap,
    },
    ringKcal: { fontSize: m.ring_kcal_font, lineHeight: m.ring_kcal_line, fontWeight: '800', letterSpacing: -0.5 },
    ringKcalLabel: {
      fontSize: m.ring_kcal_label_font,
      lineHeight: m.ring_kcal_label_line,
      fontWeight: '700',
      marginTop: 0,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
    },
    ringGoal: { fontSize: m.ring_goal_font, lineHeight: m.ring_goal_line, fontWeight: '600', marginTop: m.macro_line_inner_gap },
    macroBlockWrap: {
      flex: 1,
      minWidth: 0,
      height: m.macro_block_height,
    },
    healthInfoBtn: {
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    healthInfoMark: {
      fontSize: m.health_info_mark_font,
      lineHeight: m.health_info_mark_line,
      fontWeight: '800',
      textAlign: 'center',
    },
    macroBlock: {
      flex: 1,
      minWidth: 0,
      height: m.macro_block_height,
      gap: m.macro_line_gap,
    },
    macroLine: {
      flex: 1,
      minHeight: 0,
      gap: m.macro_line_inner_gap,
      justifyContent: 'space-between',
    },
    macroLineHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    macroLineShort: {
      fontSize: m.macro_short_font,
      lineHeight: m.macro_short_line,
      fontWeight: '800',
      width: m.macro_short_width,
    },
    macroLineGrams: { fontSize: m.macro_grams_font, lineHeight: m.macro_grams_line, fontWeight: '800' },
    macroLineTrack: {
      flex: 1,
      minHeight: m.macro_track_min_height,
      borderRadius: 100,
      overflow: 'hidden',
    },
    macroLineFill: { borderRadius: 100 },
    macroLineLabel: { fontSize: m.macro_label_font, lineHeight: m.macro_label_line, fontWeight: '600' },
  });
}
